import type {
	LabeledValuePayload,
	UsageSettingPayload,
} from '@Application/Ai/AiConfiguration/Assignments/Actions/modelAssignmentsFetched';

/**
 * Immutable View, generic over the setting's value type and its bounds shape: one class serves all four
 * call settings (`vocabularyTerms`, `contextPrompt`, `systemPrompt`, `temperature`) rather than four
 * near-duplicates. The wire payload is flat; `bounds` is where this View regroups the setting-specific
 * limits (`maxTerms`/`maxTermLength`, or none for a prompt) that travel alongside `own`/`effective` on
 * the wire.
 */
class UsageSettingView<TValue, TBounds extends object> {
	readonly own: TValue | null;
	readonly effective: TValue | null;
	readonly effectiveLevel: LabeledValuePayload | null;
	readonly applicable: boolean;
	readonly editable: boolean;
	readonly supported: boolean;
	readonly unsupportedReason: string | null;
	readonly bounds: TBounds;

	constructor(payload: UsageSettingPayload<TValue>, bounds: TBounds) {
		this.own = payload.own;
		this.effective = payload.effective;
		this.effectiveLevel = payload.effectiveLevel;
		this.applicable = payload.applicable;
		this.editable = payload.editable;
		this.supported = payload.supported;
		this.unsupportedReason = payload.unsupportedReason;
		this.bounds = bounds;

		Object.freeze(this);
	}

	// `own === null` alone is not enough: `effective === null` means nobody decided at any level, which
	// is not an inheritance. An `effective` of `[]`/`''` with a non-null `effectiveLevel` is an
	// inheritance — "inherited from the account: no terms".
	get isInherited(): boolean {
		return this.own === null && this.effective !== null;
	}

	get isOverridden(): boolean {
		return this.own !== null;
	}

	get isInConflict(): boolean {
		return this.own !== null && !this.supported;
	}

	get isWritable(): boolean {
		return this.applicable && this.editable && this.supported;
	}
}

export default UsageSettingView;
